import { Modal } from '@mantine/core';
import { useState } from 'react';
import { Button } from './Button';
import { Icon } from './Icon';
import { fmt } from '@/lib/format';

type Props = {
  opened: boolean;
  onClose: () => void;
  filename: string;
  itemsAccepted: number;
  loading?: boolean;
  /** Message de refus renvoyé par le serveur lors de la dernière tentative. */
  error?: string | null;
  onConfirm: (input: { reason: string; force: boolean }) => void;
};

/**
 * Retrait d'un import : ce que le lot a écrit est SUPPRIMÉ du journal.
 *
 * Deux garde-fous seulement, parce qu'on retire un lot identifié et pas la
 * base : un motif obligatoire (il reste sur la ligne d'import, c'est la seule
 * trace qui survit) et, pour un lot historique reconstitué par fenêtre, un
 * refus motivé du serveur qu'il faut lever explicitement avec « forcer ».
 * Pas d'aperçu obligatoire : le décompte arrive dans le rapport, après coup.
 */
export function RollbackImportModal({
  opened,
  onClose,
  filename,
  itemsAccepted,
  loading,
  error,
  onConfirm,
}: Props) {
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  // Le serveur n'accepte « forcer » que s'il l'a lui-même proposé : la case
  // n'apparaît qu'après un refus qui le mentionne.
  const canForce = Boolean(error && /force/i.test(error));
  const ready = reason.trim().length >= 3 && !loading;

  return (
    <Modal opened={opened} onClose={onClose} size={560} centered title="Retirer ce lot du journal ?">
      <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
        Les événements écrits par <strong style={{ fontFamily: 'var(--mono)' }}>{filename}</strong>
        {itemsAccepted > 0 ? <> ({fmt(itemsAccepted)} lignes acceptées)</> : null} seront supprimés.
        Les fiches concernées seront recalculées à partir de ce qui leur reste ; celles qui
        n'existaient que par ce lot disparaîtront. Les événements des autres imports et des
        applications ne sont pas touchés.
      </div>

      <div className="field" style={{ marginTop: 16, marginBottom: 14 }}>
        <label>
          Motif du retrait
          <span className="muted" style={{ fontWeight: 600 }}> — conservé sur la ligne d'import</span>
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Ex : mauvais fichier, colonnes décalées — le lot corrigé sera réimporté"
          maxLength={500}
          autoFocus
        />
      </div>

      {error && (
        <div className="confirm-warn" style={{ marginBottom: 14 }}>
          <Icon name="alert" size={16} />
          <div>
            <strong>Le serveur a refusé le retrait.</strong> {error}
          </div>
        </div>
      )}

      {canForce && (
        <label
          style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 14, fontSize: 13, cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            <strong>Forcer le retrait.</strong> Seules les lignes correspondant aux critères du
            connecteur seront supprimées ; celles nommées dans le refus resteront en place.
          </span>
        </label>
      )}

      <div className="confirm-warn">
        <Icon name="alert" size={16} />
        <div>
          <strong>Cette action ne se défait pas.</strong> Le journal ne garde pas les événements
          retirés — seule la ligne d'import en conserve la trace (qui, quand, pourquoi, combien).
          Une sauvegarde antérieure au retrait les contient encore : la restaurer les ramènerait.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <Button variant="ghost" onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="alert"
          icon="trash"
          disabled={!ready || (canForce && !force)}
          loading={loading}
          onClick={() => onConfirm({ reason: reason.trim(), force: canForce && force })}
        >
          {canForce ? 'Forcer le retrait' : 'Retirer le lot'}
        </Button>
      </div>
    </Modal>
  );
}
